// packages/studio/web/src/proposals/ProposeModal.tsx
//
// Names + proposes a draft changeset: forks a branch, squashes the drafts into
// one commit, opens the proposal. Collects the authored change content welded
// into git — a title (commit subject), a description (commit body), and a
// target (the package/space the change is scoped to, which also homes an
// optional change document). The target defaults to the primary package (the
// owner of the touched files) and is overridable to any package or space.

import { FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "../ui/Modal";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toast";
import {
  discoverContainers,
  patchChangeset,
  proposeChangeset,
  type Changeset,
  type Container,
} from "./proposalsApi";
import { encodeTarget, primaryPackageTarget } from "./changeDoc";
import { useTranslation } from "../i18n";

export interface ProposeModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly slug: string;
  readonly repo: string;
  readonly changesetId: string;
  readonly initialTitle?: string;
  /** Trunk ref — the tree the target picker discovers packages/spaces from. */
  readonly baseRef: string;
  /** Draft paths touched by this changeset — drives the default target. */
  readonly touchedPaths: readonly string[];
  /** Awaited before proposing — the host flushes pending draft autosaves here
   *  so an edit made within the debounce window lands in the proposal. */
  readonly onBeforePropose?: () => Promise<void>;
  readonly onSuccess: (cs: Changeset) => void;
}

type Translate = ReturnType<typeof useTranslation>["t"];

function packageLabel(p: Container, t: Translate): string {
  if (p.name) return t("propose.packageSuffix", { name: p.name });
  if (p.path === "") return t("propose.packageRoot");
  return t("propose.packageSuffix", { name: p.path });
}
function spaceLabel(s: Container, t: Translate): string {
  return t("propose.spaceSuffix", { name: s.name || s.path });
}

export function ProposeModal({
  open,
  onClose,
  slug,
  repo,
  changesetId,
  initialTitle,
  baseRef,
  touchedPaths,
  onBeforePropose,
  onSuccess,
}: ProposeModalProps) {
  const { t } = useTranslation("proposals");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(initialTitle ?? "");
  const [description, setDescription] = useState("");
  // null = user hasn't chosen; the default is applied once discovery lands.
  const [target, setTarget] = useState<string | null>(null);

  // Re-seed on (re)open: the title always follows initialTitle; description +
  // target are PER-CHANGESET authored content (welded into that changeset's
  // commit body / scope), so they reset when the modal opens for a DIFFERENT
  // changeset than they were authored for — without this, a cached changeset
  // switch (the modal never unmounts) prefills B's form with A's description
  // and pins A's target over B's own primary-package default.
  const authoredFor = useRef(changesetId);
  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle ?? "");
    if (authoredFor.current !== changesetId) {
      authoredFor.current = changesetId;
      setDescription("");
      setTarget(null); // re-defaults from THIS changeset's touched files
    }
  }, [open, initialTitle, changesetId]);

  const containers = useQuery({
    queryKey: ["containers", slug, repo, baseRef],
    queryFn: () => discoverContainers(slug, repo, baseRef),
    enabled: open,
  });

  // Preselect the primary package (owner of touched files) once discovery lands
  // and the user hasn't picked yet.
  useEffect(() => {
    if (target === null && containers.data) {
      setTarget(primaryPackageTarget(touchedPaths, containers.data.packages));
    }
  }, [target, containers.data, touchedPaths]);

  const mutation = useMutation({
    mutationFn: async (t: string) => {
      // Flush pending draft autosaves so an in-debounce edit is part of the
      // proposal (same guarantee as Update).
      await onBeforePropose?.();
      // Persist the target + description first (DB scope metadata), then propose
      // — propose composes the commit message from the DB title + description.
      // With no explicit pick yet (submit raced ahead of discovery), resolve
      // the SAME default the effect would have applied — awaiting discovery
      // when needed — instead of silently persisting the root package. Only a
      // failed discovery falls back to root (nothing better is knowable).
      let resolvedTarget = target;
      if (resolvedTarget === null) {
        // fetchQuery on the SAME key joins the useQuery's in-flight request —
        // a bare discoverContainers call here fired a duplicate trunk scan.
        const data =
          containers.data ??
          (await queryClient
            .fetchQuery({
              queryKey: ["containers", slug, repo, baseRef],
              queryFn: () => discoverContainers(slug, repo, baseRef),
            })
            .catch(() => null));
        resolvedTarget = data
          ? primaryPackageTarget(touchedPaths, data.packages)
          : encodeTarget("pkg", "");
      }
      await patchChangeset(slug, repo, changesetId, {
        description: description.trim(),
        target: resolvedTarget,
      });
      return proposeChangeset(slug, repo, changesetId, { title: t, description: description.trim() });
    },
    onSuccess: (cs) => {
      toast.success(t("propose.toastProposed", { title: cs.title }));
      onSuccess(cs);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const trimmed = title.trim();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!trimmed) return;
    mutation.mutate(trimmed);
  }

  const pkgs = containers.data?.packages ?? [];
  const spaces = containers.data?.spaces ?? [];

  return (
    <Modal open={open} onClose={onClose} title={t("propose.title")}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-slate-400">
          {t("propose.intro")}
        </p>
        <Field label={t("propose.titleLabel")}>
          {({ id }) => (
            <TextInput
              id={id}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("propose.titlePlaceholder")}
              autoFocus
            />
          )}
        </Field>
        <Field label={t("propose.descriptionLabel")}>
          {({ id }) => (
            <textarea
              id={id}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("propose.descriptionPlaceholder")}
              rows={4}
              className="block w-full resize-y rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:border-arch-500 focus:outline-none focus:ring-2 focus:ring-arch-500/25"
            />
          )}
        </Field>
        <Field label={t("propose.targetLabel")}>
          {({ id }) => (
            <select
              id={id}
              value={target ?? ""}
              onChange={(e) => setTarget(e.target.value)}
              disabled={containers.isLoading}
              className="block w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 transition-colors focus:border-arch-500 focus:outline-none focus:ring-2 focus:ring-arch-500/25 disabled:opacity-50"
            >
              {pkgs.map((p) => (
                <option key={`pkg:${p.path}`} value={encodeTarget("pkg", p.path)}>
                  {packageLabel(p, t)}
                </option>
              ))}
              {spaces.map((s) => (
                <option key={`space:${s.path}`} value={encodeTarget("space", s.path)}>
                  {spaceLabel(s, t)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("propose.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={!trimmed || mutation.isPending}>
            {mutation.isPending ? t("propose.submitting") : t("propose.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
